/**
 * The manual-upload queue's pure logic: what the browser will accept before it
 * bothers the server, and how a submission's outcome reads to a person.
 *
 * The limits mirror the backend's ingestion validation (`allowed_mime_types`,
 * `max_upload_bytes`). They exist here only to fail fast and explain *why* — the
 * server remains the authority and re-checks every byte it is given, so a
 * divergence costs a clearer message, never a wrongly accepted file.
 */
import { ApiError, ApiNetworkError, ApiTimeoutError } from '../../api/errors';
import type { DocumentStatusView } from '../../api/schema';

/** The media types the ingestion service accepts (backend `allowed_mime_types`). */
export const ACCEPTED_MIME: readonly string[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/tiff',
  'text/plain',
];

/** The `accept` attribute for the file picker — types plus their usual extensions. */
export const ACCEPT_ATTR = [...ACCEPTED_MIME, '.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff', '.txt'].join(
  ',',
);

/** The per-file ceiling (backend `max_upload_bytes`, 50 MiB). */
export const MAX_UPLOAD_BYTES = 52_428_800;

/** The bare media type, lower-cased and without parameters (mirrors `normalise_mime`). */
export function normaliseMime(mime: string): string {
  return (mime.split(';', 1)[0] ?? '').trim().toLowerCase();
}

/** A human byte size: `1.4 MB`, `812 KB`, `48 B`. */
export function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Why this file cannot be submitted, or `null` when it can. Checked before the
 * upload starts so an obviously doomed 40 MB push never leaves the machine.
 */
export function rejectionOf(file: File): string | null {
  if (file.size === 0) return 'This file is empty.';
  if (file.size > MAX_UPLOAD_BYTES) {
    return `${sizeLabel(file.size)} is over the ${sizeLabel(MAX_UPLOAD_BYTES)} limit.`;
  }
  const mime = normaliseMime(file.type);
  if (mime === '') return "Your browser couldn't identify this file's type.";
  if (!ACCEPTED_MIME.includes(mime)) return `${mime} isn't an accepted document type.`;
  return null;
}

/** One file's place in the upload queue. */
export type QueueStatus = 'queued' | 'uploading' | 'done' | 'rejected';

export interface QueueItem {
  /** Stable within a dialog session; also the submission's idempotency key. */
  id: string;
  file: File;
  status: QueueStatus;
  /** The server's answer once accepted — document id, pipeline status, duplicate flag. */
  result: DocumentStatusView | null;
  /** Why it was rejected, client-side or by the server. */
  error: string | null;
  /**
   * Whether the file was ever actually sent. It separates the two kinds of
   * rejection: one the browser made up front (wrong type, too big — retrying
   * would fail identically) from one the server made or a transport lost, which
   * is worth another go under the same idempotency key.
   */
  attempted: boolean;
}

/** True when re-sending this file could plausibly succeed. */
export function isRetryable(item: QueueItem): boolean {
  return item.status === 'rejected' && item.attempted;
}

/** True while the queue still has work the dialog must not be closed out from under. */
export function isBusy(items: readonly QueueItem[]): boolean {
  return items.some((item) => item.status === 'queued' || item.status === 'uploading');
}

/** How many files actually reached the pipeline (duplicates included — they are real answers). */
export function acceptedCount(items: readonly QueueItem[]): number {
  return items.filter((item) => item.status === 'done').length;
}

/**
 * A submission failure in the user's terms. The server's problem-details `detail`
 * is the best copy available — it names the actual rule that was broken — so it
 * wins whenever there is one; the rest are the cases where no server reply exists.
 */
export function uploadErrorMessage(error: unknown): string {
  if (error instanceof ApiTimeoutError) return 'The upload timed out. Check your connection and retry.';
  if (error instanceof ApiNetworkError) return "The upload didn't reach the server. Retry when you're back online.";
  if (error instanceof ApiError) {
    const detail = error.problem?.detail ?? error.problem?.title;
    if (typeof detail === 'string' && detail !== '') return detail;
    if (error.status === 413) return 'The server rejected this file as too large.';
    if (error.status === 415) return 'The server rejected this file type.';
    if (error.status === 429) return 'Too many submissions at once — wait a moment and retry.';
    if (error.status === 403) return "You don't have permission to submit documents.";
    return error.message;
  }
  return 'That upload could not be completed.';
}

/**
 * A per-file idempotency key. Generated once when the file joins the queue and
 * reused on retry, so re-pressing Retry after an ambiguous failure (the upload
 * may or may not have landed) answers `duplicate: true` rather than ingesting
 * the same document twice.
 */
export function newSubmissionKey(): string {
  const uuid = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (uuid?.randomUUID !== undefined) return uuid.randomUUID();
  // Older/insecure contexts have no `crypto.randomUUID`; uniqueness within one
  // dialog session is all the key needs.
  return `up-${String(Date.now())}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Build the queue entries for a freshly picked or dropped file list. */
export function toQueueItems(files: readonly File[]): QueueItem[] {
  return files.map((file) => {
    const rejection = rejectionOf(file);
    return {
      id: newSubmissionKey(),
      file,
      status: rejection === null ? 'queued' : 'rejected',
      result: null,
      error: rejection,
      attempted: false,
    };
  });
}
